/**
 * MyListings — Seller's current listings with status management.
 *
 * Draft and withdrawn listings can be deleted (legal copy §15.3): the
 * confirmation (DeleteListingDialog) says what the delete does, the server
 * re-checks it, and on success the list is refreshed with legal's
 * `GEN.listing.delete.done`.
 */

import React, { useState, useEffect, useCallback, memo, useRef, lazy, Suspense } from 'react';
import APIAgent from '../../agents/apiAgent';
import {
  LISTING_HAS_OPEN_OFFERS_FALLBACK,
  PAPERWORK_CATEGORY_REQUIRED,
  hasOpenOffersRefusal,
  isDisclosureSigning,
  isListingUnderContract,
  listingUnderContractMessage,
  publishRefusalCode,
  refusalMessage,
} from './publishGate';
import { copyText } from './paperworkCopy';
import { disclosureSigningText } from './paperworkPresentation';
import { formatDollars, listingFeeCents } from '../../utils/pricing';
import { canDeleteListing } from '../../agents/listingDeleteAgent';
import DeleteListingDialog, { DeleteListingButton, ListingDeletedNotice } from './DeleteListingDialog';
import { listingDeleteCopy } from './listingDeleteCopy';
import ShareListingButton, { isShareableListing } from '../Public/ShareListingButton';
import { usePublicSiteEnabled } from '../../hooks/usePublicSiteEnabled';
import './MyListings.css';

// Lazy-loaded so @stripe/react-stripe-js + Stripe.js only load when a seller
// actually reaches the Full Service payment step (never on buyer-side pages).
const OneTimeChargeModal = lazy(() => import('../Billing/OneTimeChargeModal'));

/**
 * Detect the Full Service payment-required gate on a publish attempt.
 *
 * The backend returns 402 with detail.code === 'full_service_payment_required'
 * ONLY when billing is enabled (dark by default — see houses.py). Matching on it
 * is therefore inert when billing is off: no 402 ever fires, so the publish flow
 * is unchanged. The detail object is serialized into err.message by APIAgent.
 */
function isFullServicePaymentRequired(err) {
  if (!err || (err.status ?? err.statusCode) !== 402) return false;
  const msg = typeof err.message === 'string' ? err.message : '';
  return msg.includes('full_service_payment_required') || msg.toLowerCase().includes('full service payment required');
}

/**
 * The amount still due (cents) from a payment-required 402, or null when the
 * detail doesn't carry one. A draft paid at $799 and then priced at $1M+ owes
 * only the difference, so this can be less than the tier fee.
 */
function amountDueFromError(err) {
  const due = err?.detail?.amount_due_cents;
  return typeof due === 'number' ? due : null;
}

// Publish-gate refusal code → the card banner that explains it.
const PUBLISH_REFUSAL_KINDS = {
  listing_agreement_required: 'agreement',
  listing_price_required: 'price',
  condition_report_required: 'report',
  // Paperwork (design §8.2) and the interim WI condo addendum upload.
  paperwork_category_required: 'paperwork',
  paperwork_category_unsupported: 'paperwork',
  required_upload_missing: 'upload',
  // A required Property Features answer is blank (a listing that went live
  // before every field was required, relisted after a withdraw).
  property_features_incomplete: 'features',
  // Ownership verification (docs/plans/ownership-verification/README.md).
  ownership_verification_required: 'ownership',
};

/**
 * Ownership-refusal banner copy, keyed by the `verification_status` the 409
 * carries. Under review → nothing for the seller to do but wait; everything
 * else is fixed in the wizard's Confirm Ownership step.
 */
function ownershipBanner(verificationStatus) {
  if (verificationStatus === 'needs_review' || verificationStatus === 'processing') {
    return {
      text: "Your ownership documents are under review. You can publish once the review is complete.",
      cta: 'View review status',
    };
  }
  if (verificationStatus === 'rejected') {
    return {
      text: "We couldn't confirm your ownership or authority to sell this property, so this listing can't be published.",
      cta: 'View details',
    };
  }
  if (verificationStatus === 'revoked') {
    return {
      text: 'We need to review your ownership documents again before this listing can be published.',
      cta: 'Upload documents',
    };
  }
  if (verificationStatus === 'more_info_requested') {
    return {
      text: 'We need more information to finish reviewing your ownership documents before this listing can be published.',
      cta: 'Upload documents',
    };
  }
  return {
    text: 'Confirm your ownership or authority to sell this property before publishing this listing.',
    cta: 'Confirm ownership',
  };
}

// Kinds whose banner shows the refusal's own message when it has one.
const SERVER_MESSAGE_KINDS = new Set([
  'report',
  'report_signing',
  'paperwork',
  'upload',
  'features',
  'under_contract',
  'open_offers',
]);

/**
 * Shown on a card whose listing is under contract (`under_contract` from the
 * seller listings read): the server refuses every move but `pending` (409
 * listing_under_contract, QA S13), so Relist / Mark Sold / Withdraw are hidden.
 * Wording per legal review 2026-10-19: "show as sold" is the platform status,
 * not the legal closing.
 */
export const UNDER_CONTRACT_NOTE =
  "Under contract. While it's under contract, this listing can't be relisted, withdrawn or marked sold here. It will show as sold once you and the buyer both finish the closing checklist.";
/** Added to the note only where offers are still taken (active / pending). */
export const UNDER_CONTRACT_BACKUP_OFFERS_NOTE = 'Buyers can still send backup offers.';
const BACKUP_OFFER_STATUSES = new Set(['active', 'pending']);

/** `{title}` when neither the refusal nor the listing names the report. */
const DEFAULT_DISCLOSURE_TITLE = 'Real Estate Condition Report';

/**
 * WI.disclosure.mylistings_banner.text: the fallback when a
 * condition_report_required refusal carries no message of its own.
 */
export const reportRequiredText = (title) =>
  `Complete your ${title} before publishing this listing.`;

/**
 * The report banner's text: the server's refusal message (it names the right
 * form and says publishing first is a cutthe6.com rule), else the fallback
 * with the report's title from the refusal or the listing. A report sent to
 * DocuSign but not signed yet (`disclosure_status: "signing"`) says so.
 */
export function reportBannerText(err, house) {
  const detail = err?.detail && typeof err.detail === 'object' ? err.detail : {};
  if (typeof detail.message === 'string' && detail.message.trim()) return detail.message.trim();
  const title = detail.disclosure_title || house?.paperwork?.disclosure_title || DEFAULT_DISCLOSURE_TITLE;
  if (isDisclosureSigning(detail.disclosure_status)) return disclosureSigningText(title);
  return reportRequiredText(title);
}

// Banners for refusals fixed by finishing the listing in the wizard.
const FINISH_LISTING_BANNERS = {
  price: {
    icon: '💲',
    text: 'Set an asking price before publishing this listing.',
    cta: 'Add a price',
    testId: 'publish-price-required-banner',
  },
  report: {
    icon: '📋',
    // Text comes from reportBannerText (the server's message) at refusal time.
    text: reportRequiredText(DEFAULT_DISCLOSURE_TITLE),
    cta: 'Finish the report',
    testId: 'publish-report-required-banner',
    // Opens the listing at its Seller Disclosure step.
    initialStep: 'disclosure',
  },
  // The report went to DocuSign but the seller hasn't signed it yet.
  report_signing: {
    icon: '✍',
    text: disclosureSigningText(DEFAULT_DISCLOSURE_TITLE),
    cta: 'Finish signing',
    testId: 'publish-report-signing-banner',
    initialStep: 'disclosure',
  },
  paperwork: {
    icon: '🏠',
    // WI.paperwork.publish.category_required (the server's message wins).
    text: 'Answer the property questions on Property Details before publishing.',
    cta: 'Finish the listing',
    testId: 'publish-paperwork-required-banner',
    // Opens the listing on Property Details, at the property-type questions.
    initialStep: 'paperwork',
  },
  features: {
    icon: '📝',
    // The server's message (with the count of blank answers) wins.
    text: 'Fill in every Property Features question before publishing this listing. Choose "None" where nothing applies.',
    cta: 'Finish Property Features',
    testId: 'publish-features-required-banner',
    // Opens the listing on Property Details, where Property Features live.
    initialStep: 'features',
  },
  upload: {
    icon: '📎',
    // WI.condo_addendum.missing (legal L4; the server's message wins).
    text: copyText('WI.condo_addendum.missing'),
    cta: 'Upload it',
    testId: 'publish-upload-required-banner',
  },
};

const STATUS_META = {
  draft: { label: 'Draft', color: '#94a3b8', icon: '📝' },
  active: { label: 'Active', color: '#10b981', icon: '🟢' },
  pending: { label: 'Pending', color: '#f59e0b', icon: '⏳' },
  sold: { label: 'Sold', color: '#8b5cf6', icon: '🎉' },
  withdrawn: { label: 'Withdrawn', color: '#6b7280', icon: '⛔' },
};

// Step hints name the sidebar items as they appear (QA F6). Step 0 is only
// current while nothing is published, so its hint is about publishing; it
// used to tell a seller with only a draft that the home was "live".
const FLOW_STEPS = [
  { id: 'listed',   icon: '🏠', label: 'Listed',          hint: 'Finish your listing and publish it so buyers can see it.' },
  { id: 'offers',   icon: '📄', label: 'Receive Offers',  hint: 'Your home is live. Buyers’ offers appear under Offers Received in the sidebar.' },
  { id: 'negotiate',icon: '🤝', label: 'Negotiate',       hint: 'Counter, accept, or reject offers under Offers Received → Negotiations.' },
  { id: 'docs',     icon: '📋', label: 'Sign Docs',       hint: 'You accepted an offer. Review and e-sign agreements in Documents (under More).' },
  { id: 'close',    icon: '✅', label: 'Close',           hint: 'Congrats — your home is sold!' },
];

// Map listing status → which FLOW_STEPS index is currently active (0-based).
// A pending listing is actively negotiating offers (opening negotiations moves
// it to pending); its live stage is Negotiate, not the paperwork — the listing
// status alone can't say an offer was accepted, so the flow no longer claims it.
export function activeFlowStep(listings) {
  const hasSold    = listings.some((l) => l.listing_status === 'sold');
  const hasPending = listings.some((l) => l.listing_status === 'pending');
  const hasActive  = listings.some((l) => l.listing_status === 'active');
  if (hasSold)    return 4; // Close
  if (hasPending) return 2; // Negotiate
  if (hasActive)  return 1; // Receive Offers
  return 0;                 // Not published yet (drafts only)
}

const TransactionFlowBanner = memo(function TransactionFlowBanner({ listings, onViewJourney }) {
  const currentStep = activeFlowStep(listings);
  const step = FLOW_STEPS[currentStep];
  // Pick the most relevant house to link to: active → pending → sold
  const journeyHouse =
    listings.find((l) => l.listing_status === 'active') ||
    listings.find((l) => l.listing_status === 'pending') ||
    listings.find((l) => l.listing_status === 'sold');
  const canViewJourney = !!journeyHouse && typeof onViewJourney === 'function';
  return (
    <div className="ml-flow-banner">
      <div className="ml-flow-banner__top">
        <p className="ml-flow-banner__label">Your transaction journey</p>
        {canViewJourney && (
          <button
            className="ml-flow-banner__view-btn"
            onClick={() => onViewJourney(journeyHouse)}
          >
            View Journey →
          </button>
        )}
      </div>
      <div className="ml-flow-banner__steps">
        {FLOW_STEPS.map((s, idx) => {
          const done    = idx < currentStep;
          const active  = idx === currentStep;
          return (
            <React.Fragment key={s.id}>
              <div className={`ml-flow-step ${
                done    ? 'ml-flow-step--done'
                : active ? 'ml-flow-step--active'
                         : 'ml-flow-step--upcoming'
              }`}>
                <span className="ml-flow-step__icon">{done ? '✓' : s.icon}</span>
                <span className="ml-flow-step__label">{s.label}</span>
              </div>
              {idx < FLOW_STEPS.length - 1 && (
                <div className={`ml-flow-connector ${done ? 'ml-flow-connector--done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <p className="ml-flow-banner__hint">💡 {step.hint}</p>
    </div>
  );
});

export default function MyListings({ sellerId, onCreateNew, onEditListing, onNavigateToNegotiations, onViewJourney, sharedListings, setSharedListings, refreshToken = 0 }) {
  // Share links point at the public /homes/:id page, which only exists while
  // PUBLIC_SITE_ENABLED is on (provided by App.js).
  const publicSiteEnabled = usePublicSiteEnabled();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localListings, setLocalListings] = useState([]);
  const hasMountedRef = useRef(false);

  const listings = Array.isArray(sharedListings) ? sharedListings : localListings;
  const updateListings = typeof setSharedListings === 'function' ? setSharedListings : setLocalListings;

  // Full Service charge (PR5): which house is mid-payment, and which drafts have
  // hit the 402 payment gate (so they render the draft-until-paid banner). Both
  // stay empty when billing is disabled — the 402 only fires when billing is on,
  // so the publish flow is unchanged while billing is dark.
  const [chargeHouse, setChargeHouse] = useState(null);
  // houseId → amount due in cents (null when the 402 didn't say) for drafts
  // that hit the Full Service payment gate.
  const [paymentNeededIds, setPaymentNeededIds] = useState(() => new Map());
  // houseId → 'agreement' | 'generic' for the last failed status change.
  const [statusErrors, setStatusErrors] = useState({});
  // houseId → the refusal's own message, for banners that show it.
  const [refusalMessages, setRefusalMessages] = useState({});
  // houseId → { status, message } from an ownership_verification_required 409
  // (`detail.verification_status`, `detail.seller_message`).
  const [ownershipRefusals, setOwnershipRefusals] = useState({});
  // Listing delete (copy §15.3): the listing in the confirmation, and the success line.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteNotice, setDeleteNotice] = useState(null);

  const retryFetchListings = useCallback(() => {
    if (sellerId == null) {
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    APIAgent.getSellerListings(sellerId)
      .then((result) => updateListings(result?.houses || []))
      .catch((err) => {
        console.error('Error fetching listings:', err);
        setError('Failed to load your listings');
      })
      .finally(() => setLoading(false));
  }, [sellerId, updateListings]);

  useEffect(() => {
    if (sellerId == null) {
      setLoading(false);
      setError(null);
      return;
    }
    if (listings.length > 0) {
      setLoading(false);
      setError(null);
      return;
    }
    retryFetchListings();
  }, [sellerId, listings.length, retryFetchListings]);

  /** Re-read the listings without the full-page spinner (after a delete). */
  const refreshListingsQuietly = useCallback(() => {
    if (sellerId == null) return;
    APIAgent.getSellerListings(sellerId)
      .then((result) => updateListings(result?.houses || []))
      .catch((err) => console.error('Error refreshing listings:', err));
  }, [sellerId, updateListings]);

  const handleDeleted = useCallback((houseId) => {
    setDeleteTarget(null);
    setDeleteNotice(listingDeleteCopy('GEN.listing.delete.done'));
    updateListings((prev) => prev.filter((h) => h.id !== houseId));
    refreshListingsQuietly();
  }, [updateListings, refreshListingsQuietly]);

  const deleteDialog = deleteTarget && (
    <DeleteListingDialog
      house={deleteTarget}
      onClose={() => setDeleteTarget(null)}
      onDeleted={handleDeleted}
    />
  );
  const deleteNoticeLine = deleteNotice && <ListingDeletedNotice>{deleteNotice}</ListingDeletedNotice>;

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (sellerId == null) {
      return;
    }

    retryFetchListings();
  }, [refreshToken, sellerId, retryFetchListings]);

  /**
   * Change a listing's status. Resolves to null on success, else the kind of
   * refusal shown on the card ('agreement', 'paperwork', 'under_contract',
   * 'generic', ...), so callers can decide what happens next.
   */
  const handleStatusChange = async (houseId, newStatus) => {
    setStatusErrors(prev => {
      if (!(houseId in prev)) return prev;
      const next = { ...prev };
      delete next[houseId];
      return next;
    });
    try {
      await APIAgent.updateListingStatus(houseId, newStatus);
      updateListings(prev => prev.map(h => h.id === houseId ? { ...h, listing_status: newStatus } : h));
      // A successful publish clears any prior payment-needed flag for this house.
      setPaymentNeededIds(prev => {
        if (!prev.has(houseId)) return prev;
        const next = new Map(prev);
        next.delete(houseId);
        return next;
      });
      return null;
    } catch (err) {
      // Full Service payment gate (billing-on only): open the charge modal
      // instead of surfacing a generic error. Inert when billing is disabled.
      if (newStatus === 'active' && isFullServicePaymentRequired(err)) {
        setPaymentNeededIds(prev => {
          const next = new Map(prev);
          next.set(houseId, amountDueFromError(err));
          return next;
        });
        const house = listings.find(h => h.id === houseId) || { id: houseId };
        setChargeHouse(house);
        return 'payment';
      }
      console.error('Error updating status:', err);
      // Without this the card's Publish button failed silently: the backend
      // 409s a listing that isn't ready (unsigned agreement, no price, or an
      // unfinished WI condition report) and the seller saw nothing happen.
      // Entering `pending` (Negotiate) can be refused too: a WI listing needs
      // its property type confirmed first (409 paperwork_category_required).
      let refusal = null;
      if (newStatus === 'active') refusal = publishRefusalCode(err);
      else if (newStatus === 'pending' && publishRefusalCode(err) === PAPERWORK_CATEGORY_REQUIRED) {
        refusal = PAPERWORK_CATEGORY_REQUIRED;
      }
      // Any move but `pending` while under contract (withdraw, back to draft,
      // relist, mark sold): 409 listing_under_contract, with the server's
      // message. The card then hides the moves it can't make.
      const underContract = isListingUnderContract(err);
      if (underContract) {
        updateListings(prev => prev.map(h => (h.id === houseId ? { ...h, under_contract: true } : h)));
      }
      // Mark Sold while an offer could still be accepted: 409
      // listing_has_open_offers (accepting it later would put a sold listing
      // under contract with no close).
      const openOffers = !underContract && hasOpenOffersRefusal(err);
      let kind = underContract
        ? 'under_contract'
        : openOffers ? 'open_offers' : PUBLISH_REFUSAL_KINDS[refusal] || 'generic';
      // Sent to DocuSign but not signed: "Finish signing", not "Finish the report".
      if (kind === 'report' && isDisclosureSigning(err?.detail?.disclosure_status)) {
        kind = 'report_signing';
      }
      if (refusal === 'ownership_verification_required') {
        setOwnershipRefusals(prev => ({
          ...prev,
          [houseId]: {
            status: err?.detail?.verification_status || null,
            message: typeof err?.detail?.seller_message === 'string' ? err.detail.seller_message : null,
          },
        }));
      }
      setStatusErrors(prev => ({
        ...prev,
        [houseId]: kind,
      }));
      const house = listings.find(h => h.id === houseId);
      if (kind === 'report' || kind === 'report_signing') {
        setRefusalMessages(prev => ({ ...prev, [houseId]: reportBannerText(err, house) }));
      } else if (kind === 'under_contract') {
        setRefusalMessages(prev => ({
          ...prev,
          [houseId]: listingUnderContractMessage(err, house?.state, newStatus),
        }));
      } else if (kind === 'open_offers') {
        setRefusalMessages(prev => ({ ...prev, [houseId]: refusalMessage(err) || LISTING_HAS_OPEN_OFFERS_FALLBACK }));
      } else if (SERVER_MESSAGE_KINDS.has(kind)) {
        setRefusalMessages(prev => ({ ...prev, [houseId]: refusalMessage(err) }));
      }
      return kind;
    }
  };

  /** Open the Full Service charge modal for a draft (banner CTA + publish 402). */
  const openChargeFor = useCallback((house) => {
    setChargeHouse(house);
  }, []);

  /** After a successful charge, finalize the publish (status → active). */
  const handleChargeSucceeded = useCallback(async () => {
    const house = chargeHouse;
    if (!house) return;
    try {
      await APIAgent.updateListingStatus(house.id, 'active');
      updateListings(prev => prev.map(h => h.id === house.id ? { ...h, listing_status: 'active' } : h));
    } catch (err) {
      // The webhook may not have stamped the paid flag yet; refetch so the card
      // reflects the latest server state rather than getting stuck.
      console.error('Publish after charge failed; refetching listings:', err);
      retryFetchListings();
    } finally {
      setPaymentNeededIds(prev => {
        if (!prev.has(house.id)) return prev;
        const next = new Map(prev);
        next.delete(house.id);
        return next;
      });
    }
  }, [chargeHouse, updateListings, retryFetchListings]);

  const handleNegotiate = async (house) => {
    const refused = await handleStatusChange(house.id, 'pending');
    // The property type must be confirmed before the listing can take offers:
    // stay here, where the banner sends the seller to the property questions.
    if (refused === 'paperwork') return;
    if (typeof onNavigateToNegotiations === 'function') {
      onNavigateToNegotiations({
        propertyId: house.id,
        propertyAddress: house.address,
      });
    }
  };

  if (loading) {
    return (
      <div className="my-listings__loading">
        <div className="my-listings__spinner" />
        <p>Loading your listings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-listings__error">
        <p>{error}</p>
        <button className="my-listings__btn my-listings__btn--secondary" onClick={retryFetchListings}>
          Retry
        </button>
      </div>
    );
  }

  if (sellerId == null && listings.length === 0) {
    return (
      <div className="my-listings__empty">
        <div className="my-listings__empty-icon">⏳</div>
        <h3>Seller profile not ready</h3>
        <p>We couldn't load your seller account yet. Please wait a moment or reload after signing in.</p>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="my-listings__empty">
        {deleteNoticeLine}
        <div className="my-listings__empty-icon">🏠</div>
        <h3>No Listings Yet</h3>
        <p>Post your first property to start attracting buyers.</p>
        <button className="my-listings__btn my-listings__btn--primary" onClick={onCreateNew}>
          + Post a Listing
        </button>
      </div>
    );
  }

  return (
    <div className="my-listings">
      <TransactionFlowBanner listings={listings} onViewJourney={onViewJourney} />
      {deleteNoticeLine}
      <div className="my-listings__header">
        <div>
          <h3 className="my-listings__title">My Listings ({listings.length})</h3>
        </div>
        <button className="my-listings__btn my-listings__btn--primary" onClick={onCreateNew}>
          + New Listing
        </button>
      </div>

      <div className="my-listings__grid">
        {listings.map((house) => {
          const status = STATUS_META[house.listing_status] || STATUS_META.draft;
          // Under contract (QA S13): only `pending` is reachable, so the
          // relist / withdraw / mark-sold / publish buttons are hidden.
          const underContract = house.under_contract === true;
          const imgUrl =
            house.image_urls?.length > 0
              ? APIAgent.getImageUrl(house.image_urls[0])
              : null;

          return (
            <div className="my-listings__card" key={house.id}>
              <div className="my-listings__card-img">
                {imgUrl ? (
                  <img src={imgUrl} alt={house.address} />
                ) : (
                  <div className="my-listings__card-img-placeholder">🏡</div>
                )}
                <span
                  className="my-listings__badge"
                  style={{ background: status.color }}
                >
                  {status.icon} {status.label}
                </span>
              </div>
              <div className="my-listings__card-body">
                <h4 className="my-listings__card-address">{house.address}</h4>
                <p className="my-listings__card-location">
                  {house.listing_number && <span className="listing-badge">#{house.listing_number}</span>}
                  {house.city}, {house.state} {house.zip_code}
                </p>
                {house.price && (
                  <p className="my-listings__card-price">
                    ${house.price.toLocaleString()}
                  </p>
                )}
                <div className="my-listings__card-specs">
                  {house.bedrooms != null && <span>{house.bedrooms} bd</span>}
                  {house.bathrooms != null && <span>{house.bathrooms} ba</span>}
                  {house.square_feet != null && (
                    <span>{house.square_feet.toLocaleString()} sqft</span>
                  )}
                </div>

                {/* Draft-until-paid banner: shown only after a draft hit the
                    Full Service payment gate (402). Inert when billing is off —
                    a 402 never fires, so paymentNeededIds stays empty. */}
                {house.listing_status === 'draft' && paymentNeededIds.has(house.id) && (
                  <div
                    className="my-listings__draft-paid-banner"
                    role="status"
                    data-testid="draft-until-paid-banner"
                  >
                    <span className="my-listings__draft-paid-icon" aria-hidden="true">💳</span>
                    <div className="my-listings__draft-paid-body">
                      <p className="my-listings__draft-paid-text">
                        Saved as draft — complete your {formatDollars(paymentNeededIds.get(house.id) ?? listingFeeCents(house.price))} Full Service payment to publish.
                      </p>
                      <button
                        type="button"
                        className="my-listings__draft-paid-cta"
                        onClick={() => openChargeFor(house)}
                        data-testid="complete-payment-publish-btn"
                      >
                        Complete payment &amp; publish
                      </button>
                    </div>
                  </div>
                )}

                {/* Publish refused because the listing agreement is unsigned
                    (409 listing_agreement_required). The CTA reopens the
                    listing wizard; the seller continues through it to the
                    Sign Listing Agreement step, keeping each step's checks. */}
                {statusErrors[house.id] === 'agreement' && (
                  <div
                    className="my-listings__draft-paid-banner"
                    role="alert"
                    data-testid="publish-agreement-required-banner"
                  >
                    <span className="my-listings__draft-paid-icon" aria-hidden="true">✍️</span>
                    <div className="my-listings__draft-paid-body">
                      <p className="my-listings__draft-paid-text">
                        Sign your listing agreement before publishing this listing.
                      </p>
                      <button
                        type="button"
                        className="my-listings__draft-paid-cta"
                        onClick={() => onEditListing && onEditListing(house)}
                        data-testid="sign-agreement-to-publish-btn"
                      >
                        Continue to sign
                      </button>
                    </div>
                  </div>
                )}
                {FINISH_LISTING_BANNERS[statusErrors[house.id]] && (() => {
                  const banner = FINISH_LISTING_BANNERS[statusErrors[house.id]];
                  return (
                    <div
                      className="my-listings__draft-paid-banner"
                      role="alert"
                      data-testid={banner.testId}
                    >
                      <span className="my-listings__draft-paid-icon" aria-hidden="true">{banner.icon}</span>
                      <div className="my-listings__draft-paid-body">
                        <p className="my-listings__draft-paid-text">
                          {(SERVER_MESSAGE_KINDS.has(statusErrors[house.id]) && refusalMessages[house.id]) || banner.text}
                        </p>
                        <button
                          type="button"
                          className="my-listings__draft-paid-cta"
                          onClick={() =>
                            onEditListing &&
                            (banner.initialStep
                              ? onEditListing(house, { initialStep: banner.initialStep })
                              : onEditListing(house))
                          }
                        >
                          {banner.cta}
                        </button>
                      </div>
                    </div>
                  );
                })()}
                {/* Under contract: why Relist / Mark Sold / Withdraw are gone.
                    A sold listing needs no explanation; a refusal banner below
                    says it already. */}
                {underContract && house.listing_status !== 'sold' &&
                  statusErrors[house.id] !== 'under_contract' && (
                  <p className="my-listings__under-contract-note" data-testid="listing-under-contract-note">
                    <span aria-hidden="true">🔒 </span>
                    {UNDER_CONTRACT_NOTE}
                    {BACKUP_OFFER_STATUSES.has(house.listing_status) && ` ${UNDER_CONTRACT_BACKUP_OFFERS_NOTE}`}
                  </p>
                )}
                {/* A status change refused because the listing is under
                    contract (409 listing_under_contract). The server's message
                    says why. */}
                {statusErrors[house.id] === 'under_contract' && (
                  <div
                    className="my-listings__draft-paid-banner"
                    role="alert"
                    data-testid="listing-under-contract-banner"
                  >
                    <span className="my-listings__draft-paid-icon" aria-hidden="true">🔒</span>
                    <div className="my-listings__draft-paid-body">
                      <p className="my-listings__draft-paid-text">{refusalMessages[house.id]}</p>
                    </div>
                  </div>
                )}
                {/* Publish refused because ownership isn't verified (409
                    ownership_verification_required). The CTA reopens the
                    wizard straight at Confirm Ownership. */}
                {statusErrors[house.id] === 'ownership' && (() => {
                  const refusal = ownershipRefusals[house.id] || {};
                  const banner = ownershipBanner(refusal.status);
                  return (
                    <div
                      className="my-listings__draft-paid-banner"
                      role="alert"
                      data-testid="publish-ownership-required-banner"
                    >
                      <span className="my-listings__draft-paid-icon" aria-hidden="true">🏠</span>
                      <div className="my-listings__draft-paid-body">
                        <p className="my-listings__draft-paid-text">{banner.text}</p>
                        {refusal.message && (
                          <p
                            className="my-listings__draft-paid-text my-listings__ownership-note"
                            data-testid="ownership-refusal-seller-message"
                          >
                            {refusal.message}
                          </p>
                        )}
                        <button
                          type="button"
                          className="my-listings__draft-paid-cta"
                          onClick={() => onEditListing && onEditListing(house, { initialStep: 'ownership' })}
                          data-testid="finish-ownership-verification-btn"
                        >
                          {banner.cta}
                        </button>
                      </div>
                    </div>
                  );
                })()}
                {statusErrors[house.id] === 'open_offers' && (
                  <div
                    className="my-listings__draft-paid-banner"
                    role="alert"
                    data-testid="listing-open-offers-banner"
                  >
                    <span className="my-listings__draft-paid-icon" aria-hidden="true">📨</span>
                    <div className="my-listings__draft-paid-body">
                      <p className="my-listings__draft-paid-text">{refusalMessages[house.id]}</p>
                    </div>
                  </div>
                )}
                {statusErrors[house.id] === 'generic' && (
                  <p className="my-listings__status-error" role="alert">
                    Couldn&apos;t update this listing. Please try again.
                  </p>
                )}

                {/* Status action buttons */}
                <div className="my-listings__actions">
                  {house.listing_status === 'draft' && (
                    <>
                      <button
                        className="my-listings__action-btn my-listings__action-btn--edit"
                        onClick={() => onEditListing && onEditListing(house)}
                      >
                        ✏️ Edit
                      </button>
                      {!underContract && (
                        <button
                          className="my-listings__action-btn my-listings__action-btn--publish"
                          onClick={() => handleStatusChange(house.id, 'active')}
                        >
                          🚀 Publish
                        </button>
                      )}
                    </>
                  )}
                  {house.listing_status === 'active' && (
                    <>
                      <button
                        className="my-listings__action-btn my-listings__action-btn--edit"
                        onClick={() => onEditListing && onEditListing(house)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="my-listings__action-btn my-listings__action-btn--pending"
                        onClick={() => handleNegotiate(house)}
                      >
                        🤝 Negotiate
                      </button>
                      {!underContract && (
                        <button
                          className="my-listings__action-btn my-listings__action-btn--withdraw"
                          onClick={() => handleStatusChange(house.id, 'withdrawn')}
                        >
                          Withdraw
                        </button>
                      )}
                    </>
                  )}
                  {house.listing_status === 'pending' && (
                    <>
                      <button
                        className="my-listings__action-btn my-listings__action-btn--edit"
                        onClick={() => onEditListing && onEditListing(house)}
                      >
                        ✏️ Edit
                      </button>
                      {/* A listing goes `pending` the moment an offer is
                          accepted, which is exactly when the seller needs the
                          negotiation workspace — it is where the purchase
                          agreement is generated and signed. Without this the
                          card for a live deal stops linking to the deal. */}
                      <button
                        className="my-listings__action-btn my-listings__action-btn--pending"
                        onClick={() => handleNegotiate(house)}
                      >
                        🤝 Negotiate
                      </button>
                      {/* Under contract, `sold` comes from the two-party closing
                          checklist and relisting would advertise a home that's
                          already sold to someone (QA S13). */}
                      {!underContract && (
                        <>
                          <button
                            className="my-listings__action-btn my-listings__action-btn--sold"
                            onClick={() => handleStatusChange(house.id, 'sold')}
                          >
                            🎉 Mark Sold
                          </button>
                          <button
                            className="my-listings__action-btn my-listings__action-btn--relist"
                            onClick={() => handleStatusChange(house.id, 'active')}
                          >
                            Relist
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {house.listing_status === 'withdrawn' && (
                    <>
                      <button
                        className="my-listings__action-btn my-listings__action-btn--edit"
                        onClick={() => onEditListing && onEditListing(house)}
                      >
                        ✏️ Edit
                      </button>
                      {!underContract && (
                        <button
                          className="my-listings__action-btn my-listings__action-btn--relist"
                          onClick={() => handleStatusChange(house.id, 'active')}
                        >
                          Relist
                        </button>
                      )}
                    </>
                  )}
                  {/* Copy §15.3 rule 1: only a draft or withdrawn listing can be deleted. */}
                  {/* Under contract the server refuses a delete too (legacy rows only). */}
                  {canDeleteListing(house) && !underContract && (
                    <DeleteListingButton
                      house={house}
                      onClick={(h) => { setDeleteNotice(null); setDeleteTarget(h); }}
                    />
                  )}
                  {['active', 'pending', 'sold'].includes(house.listing_status) && (
                    <button
                      className="my-listings__action-btn my-listings__action-btn--journey"
                      onClick={() => onViewJourney && onViewJourney(house)}
                    >
                      🗺️ View Journey
                    </button>
                  )}
                  {/* On-market listings have a public page (/homes/:id) anyone can open. */}
                  {publicSiteEnabled && isShareableListing(house) && (
                    <ShareListingButton
                      houseId={house.id}
                      address={house.address}
                      className="my-listings__action-btn my-listings__action-btn--share"
                      showOpenLink
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {deleteDialog}

      {/* Full Service charge modal (PR5). Lazy-loaded — Stripe only loads when a
          seller reaches the payment step. Mounted once; keyed on the house being
          published. */}
      {chargeHouse && (
        <Suspense fallback={null}>
          <OneTimeChargeModal
            open={!!chargeHouse}
            houseId={chargeHouse.id}
            listingAddress={chargeHouse.address}
            listPrice={chargeHouse.price}
            onClose={() => setChargeHouse(null)}
            onSucceeded={handleChargeSucceeded}
          />
        </Suspense>
      )}
    </div>
  );
}
